import React from 'react';
import { Box, Button, Skeleton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { RotateCw } from 'lucide-react';
import { ErrorState, SectionCard, SpecGrid, TableCard, TableEmptyRow, TableSkeletonRows, useDelayedFlag } from '../../components/ui';
import { sbg } from '../../theme/sbgTokens';
import { formatCount, formatDate, formatNumber, formatPercent, MISSING } from '../../utils/format';
import type { UncountedTimePeerComparison, UncountedTimeSummary } from '../../utils/uncountedTime';
import { hrs, RegionSkeleton, TypeLabel } from './meta';
import { describeEntryText, NOT_COUNTED_FLAG } from './text';

/** Two lines, then an ellipsis; the full text stays in the DOM for screen readers. */
const CLAMP_2 = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  overflowWrap: 'anywhere',
} as const;

export interface UncountedTimeProps {
  year: number;
  summary: UncountedTimeSummary;
  comparison: UncountedTimePeerComparison;
  comparisonLoading: boolean;
  comparisonError: boolean;
  onRetryComparison: () => void;
  isJrAeView: boolean;
  loading: boolean;
  error: boolean;
  /** Why it failed and what to do, e.g. "The server didn't respond. Try again in a minute." */
  errorBody?: string;
  onRetry: () => void;
}

/** Approved PTO and sick entries under 4 hours: recorded at 0 hrs, so they never reduce a balance. */
const UncountedTime: React.FC<UncountedTimeProps> = ({
  year,
  summary,
  comparison,
  comparisonLoading,
  comparisonError,
  onRetryComparison,
  isJrAeView,
  loading,
  error,
  errorBody = "The server didn't respond. Try again in a minute.",
  onRetry,
}) => {
  const scopeLabel = isJrAeView ? 'Assigned-team' : 'Company';
  const showComparisonSkeleton = useDelayedFlag(comparisonLoading, 300);
  const comparisonValue = comparisonLoading ? (
    <Box component="span" role="status" sx={{ display: 'block' }}>
      <span className="sbg-sr-only">Loading comparison</span>
      <Skeleton
        aria-hidden="true"
        variant="rounded"
        width="70%"
        height={28}
        sx={{ visibility: showComparisonSkeleton ? 'visible' : 'hidden' }}
      />
    </Box>
  ) : comparisonError ? (
    MISSING
  ) : comparison.targetIsActive ? (
    `Rank ${formatNumber(comparison.rank)} of ${formatNumber(comparison.peerCount)}`
  ) : (
    formatCount(comparison.peerCount, 'peer')
  );
  const comparisonCaption = comparisonLoading ? undefined : comparisonError ? (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      Peer data didn't load.
      <Button variant="text" size="small" startIcon={<RotateCw size={14} />} onClick={onRetryComparison} sx={{ ml: -1 }}>
        Try again
      </Button>
    </Box>
  ) : comparison.targetIsActive ? (
    `Rank by frequency · more often than ${formatPercent(comparison.frequencyPercentile)} of ${isJrAeView ? 'assigned ' : ''}active employees`
  ) : (
    `Not active, so not ranked · more often than ${formatPercent(comparison.frequencyPercentile)} of ${isJrAeView ? 'assigned ' : ''}active employees`
  );

  return (
    <Box sx={{ display: 'grid', gap: 3 }}>
      <SectionCard
        title={`Uncounted PTO and sick time, ${year}`}
        description="Approved entries under 4 hours. They're recorded at 0 hrs, so they don't reduce a balance."
      >
        {loading ? (
          <RegionSkeleton label="Loading uncounted time" rows={3} height={24} />
        ) : error ? (
          <ErrorState compact title="Couldn't load uncounted time" body={errorBody} onRetry={onRetry} />
        ) : (
          <>
            <SpecGrid
              minColumnWidth={170}
              items={[
                {
                  label: 'Occurrences',
                  value: formatCount(summary.occurrences, 'entry', 'entries'),
                  caption: 'Approved, under 4 hours',
                },
                {
                  label: 'Known duration',
                  value: hrs(summary.knownDurationHours),
                  caption: summary.averageKnownDurationHours > 0
                    ? `${hrs(summary.averageKnownDurationHours)} on average`
                    : 'No exact duration recorded',
                },
                {
                  label: 'By type',
                  value: `${formatNumber(summary.ptoOccurrences)} PTO · ${formatNumber(summary.sickOccurrences)} sick`,
                  caption: summary.unknownDurationCount > 0
                    ? `${formatCount(summary.unknownDurationCount, 'exact duration')} unavailable`
                    : 'All durations available',
                },
                {
                  label: `${scopeLabel} comparison`,
                  value: comparisonValue,
                  caption: comparisonCaption,
                },
              ]}
            />
            {!comparisonLoading && !comparisonError && (
              <Box
                sx={{
                  mt: 3,
                  px: 2,
                  py: 1.5,
                  borderRadius: sbg.radius.lg,
                  bgcolor: sbg.bg.surfaceSubtle,
                }}
              >
                <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
                  {`${scopeLabel} average: `}
                  <Box component="span" sx={{ color: sbg.text.primary }}>
                    {`${formatNumber(comparison.occurrenceAverage, Number.isInteger(comparison.occurrenceAverage) ? 0 : 1)} ${comparison.occurrenceAverage === 1 ? 'entry' : 'entries'}`}
                  </Box>
                  {' and '}
                  <Box component="span" sx={{ color: sbg.text.primary }}>
                    {`${hrs(comparison.durationAverage)} known`}
                  </Box>
                  {` per active employee · ${formatCount(comparison.employeesWithUsage, 'active employee')} ${comparison.employeesWithUsage === 1 ? 'has' : 'have'} at least one`}
                </Typography>
              </Box>
            )}
          </>
        )}
      </SectionCard>

      {!error && (
        <TableCard
          aria-label={`Uncounted entries in ${year}`}
          maxHeight={480}
          toolbar={
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h4" component="h2" sx={{ color: sbg.text.primary }}>
                {`Entries in ${year}`}
              </Typography>
              <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary }}>
                Newest first
              </Typography>
            </Box>
          }
        >
          <Table stickyHeader sx={{ minWidth: 600 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 140 }}>Date</TableCell>
                <TableCell sx={{ width: 120 }}>Type</TableCell>
                <TableCell align="right" sx={{ width: 150 }}>Duration</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableSkeletonRows columns={4} rows={4} />
              ) : summary.events.length === 0 ? (
                <TableEmptyRow
                  colSpan={4}
                  title={`No uncounted time in ${year}`}
                  body="Approved PTO and sick entries under 4 hours appear here."
                />
              ) : (
                summary.events.map((event) => {
                  // Every row here is under 4 hours, so that flag would only repeat the page's premise.
                  const text = describeEntryText(event.notes, event.originalCalendarSubject);
                  const details = text.primary;
                  const flags = text.flags.filter((flag) => flag !== NOT_COUNTED_FLAG);
                  return (
                    <TableRow key={event.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(event.date.slice(0, 10))}</TableCell>
                      <TableCell>
                        <TypeLabel type={event.type} />
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {event.durationHours !== null ? (
                          hrs(event.durationHours)
                        ) : (
                          <>
                            <Box component="span" sx={{ display: 'block' }}>Under 4 hrs</Box>
                            <Typography variant="caption" component="span" sx={{ display: 'block', color: sbg.text.secondary }}>
                              Exact time not recorded
                            </Typography>
                          </>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 420 }}>
                        <Typography variant="body1" component="div" sx={{ color: details ? sbg.text.primary : sbg.text.secondary, ...CLAMP_2 }}>
                          {details || MISSING}
                        </Typography>
                        {flags.length > 0 && (
                          <Typography variant="body2" component="div" sx={{ color: sbg.text.secondary }}>
                            {flags.join(' · ')}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableCard>
      )}
    </Box>
  );
};

export default UncountedTime;
